import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Check, ArrowRight, Crown, Zap } from 'lucide-react';

export default function PricingPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = async (planType) => {
    if (!user) { navigate('/auth?tab=signup'); return; }
    try {
      const res = await api.post('/payments/create-checkout', {
        plan_type: planType,
        origin_url: window.location.origin
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Get started with basic access to research opportunities.',
      features: [
        'Browse all research projects',
        '2 applications per month',
        'Basic profile',
        'Community access',
      ],
      cta: user?.subscription?.plan === 'pro' ? 'Current Plan' : 'Get Started',
      action: () => navigate('/auth?tab=signup'),
      highlighted: false,
      icon: <Zap className="h-5 w-5" />,
    },
    {
      name: 'Pro Monthly',
      price: '$19.99',
      period: '/month',
      description: 'Unlimited access to all platform features.',
      features: [
        'Everything in Free',
        'Unlimited applications',
        'Access to premium projects',
        'All courses included',
        'Certificate downloads',
        'AI-powered recommendations',
        'Priority support',
      ],
      cta: user?.subscription?.plan === 'pro' ? 'Current Plan' : 'Start Pro Monthly',
      action: () => handleSubscribe('pro_monthly'),
      highlighted: true,
      icon: <Crown className="h-5 w-5" />,
      badge: 'Most Popular',
    },
    {
      name: 'Pro Yearly',
      price: '$149.99',
      period: '/year',
      description: 'Best value - save over 37% compared to monthly.',
      features: [
        'Everything in Pro Monthly',
        'Save $89.89 per year',
        'Early access to new features',
        'Exclusive webinars',
        'Annual research report',
      ],
      cta: 'Start Pro Yearly',
      action: () => handleSubscribe('pro_yearly'),
      highlighted: false,
      icon: <Crown className="h-5 w-5" />,
      badge: 'Best Value',
    },
  ];

  return (
    <div className="page-container min-h-screen" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20">
        <div className="text-center mb-14">
          <Badge className="mb-4 bg-[#1a5c5c]/10 text-[#1a5c5c] border-0">Pricing Plans</Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
            Choose Your Research Path
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-xl mx-auto">
            Start free and upgrade when you're ready. All plans include access to our growing research community.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div key={plan.name}
              className={`relative rounded-xl border p-6 md:p-8 transition-all duration-300 ${
                plan.highlighted
                  ? 'border-[#1a5c5c] shadow-[0_8px_24px_rgba(26,92,92,0.15)] scale-[1.02]'
                  : 'border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]'
              }`}
              data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}>
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className={`${plan.highlighted ? 'bg-[#1a5c5c] text-white' : 'bg-[#d4af37] text-white'} px-3 py-0.5 text-xs`}>
                    {plan.badge}
                  </Badge>
                </div>
              )}
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-4 ${plan.highlighted ? 'bg-[#1a5c5c] text-white' : 'bg-slate-100 text-slate-600'}`}>
                {plan.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{plan.name}</h3>
              <div className="mt-2 mb-4">
                <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Manrope' }}>{plan.price}</span>
                <span className="text-sm text-slate-500">{plan.period}</span>
              </div>
              <p className="text-sm text-slate-600 mb-6">{plan.description}</p>
              <ul className="space-y-2.5 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="h-4 w-4 text-[#1a5c5c] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button onClick={plan.action}
                className={`w-full ${plan.highlighted ? 'bg-[#1a5c5c] hover:bg-[#154a4a] text-white btn-teal-glow' : 'bg-white text-[#1a5c5c] border border-[#1a5c5c]/20 hover:bg-[#1a5c5c]/5'} active:scale-95 transition-transform`}
                data-testid={`plan-btn-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
                disabled={user?.subscription?.plan === 'pro' && plan.name !== 'Free'}>
                {plan.cta} {!plan.name.includes('Free') && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
